import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  FLOOR_PLAN_LIMITS,
  aspectRatioChanged,
  buildLocationTree,
  checkPlanImageSize,
  isImageInfoFailure,
  locationLabel,
  readImageInfo,
  type FloorPlan,
  type ImageInfo,
  type LocationNode,
  type LocationTreeNode,
} from '@oscom/shared';
import {
  replaceFloorPlanImage,
  uploadFloorPlan,
  uploadFloorPlanThumbnail,
} from '../api/floor-plans';
import { createThumbnail } from '../plans/thumbnail';
import { readFileBytes } from './readFileBytes';

interface LocationOption {
  id: string;
  label: string;
  depth: number;
}

function flattenLocations(entries: readonly LocationTreeNode[], depth = 0): LocationOption[] {
  return entries.flatMap((entry) => [
    { id: entry.node.id, label: locationLabel(entry.node), depth },
    ...flattenLocations(entry.children, depth + 1),
  ]);
}

const FORMAT_LABELS = { 'image/png': 'PNG', 'image/jpeg': 'JPEG' } as const;

interface Chosen {
  file: File;
  info: ImageInfo;
  previewUrl: string;
}

export interface FloorPlanUploadDialogProps {
  customerId: string;
  locations: readonly LocationNode[];
  /** Pre-selects the location (creating from a location page). */
  presetLocationId?: string;
  /** Set: the dialog replaces the image of this plan instead of creating one. */
  replacing?: FloorPlan;
  /**
   * Called with the stored plan. The caller decides what follows: the list
   * navigates to the viewer, an open viewer can switch to edit mode directly.
   */
  onDone: (plan: FloorPlan) => void;
  onCancel: () => void;
}

/** File name without its extension, used to pre-fill the plan name. */
function baseName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return (dot > 0 ? fileName.slice(0, dot) : fileName).slice(0, FLOOR_PLAN_LIMITS.maxNameLength);
}

/** Longest the optional thumbnail step may delay completing the dialog. */
const THUMBNAIL_TIMEOUT_MS = 5000;

/**
 * Adds the small preview to a just stored plan. Never fails: without a
 * thumbnail the lists show the full image, so any problem is only logged and
 * the plan as stored is returned. With one, the returned plan carries the new
 * etag of the thumbnail write.
 */
async function withThumbnail(
  customerId: string,
  plan: FloorPlan,
  file: Blob,
  info: ImageInfo,
): Promise<FloorPlan> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const step = (async () => {
      const blob = await createThumbnail(file, info);
      const response = await uploadFloorPlanThumbnail(
        customerId,
        plan.id,
        plan.currentImage.assetId,
        blob,
      );
      return response.plan;
    })();
    // A late result of a timed out step is ignored; only its rejection is silenced.
    step.catch(() => {});
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error(`timed out after ${THUMBNAIL_TIMEOUT_MS} ms`)),
        THUMBNAIL_TIMEOUT_MS,
      );
    });
    return await Promise.race([step, timeout]);
  } catch (caught: unknown) {
    console.warn('Plan thumbnail skipped', caught instanceof Error ? caught.message : caught);
    return plan;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Upload / replace dialog for plan images. The format is read from the first
 * bytes (never from the extension or `file.type`); the API repeats every check.
 */
export function FloorPlanUploadDialog({
  customerId,
  locations,
  presetLocationId,
  replacing,
  onDone,
  onCancel,
}: FloorPlanUploadDialogProps): ReactNode {
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState(presetLocationId ?? '');
  const [chosen, setChosen] = useState<Chosen | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [understood, setUnderstood] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const readCounter = useRef(0);
  const previewRef = useRef<string | undefined>(undefined);

  // Release the object URL when the dialog closes.
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  const locationOptions = flattenLocations(buildLocationTree([...locations]));
  const trimmedName = name.trim();
  const warnsAspect =
    replacing !== undefined &&
    chosen !== undefined &&
    aspectRatioChanged(replacing.currentImage, chosen.info);

  const canSubmit =
    !submitting &&
    chosen !== undefined &&
    fileError === undefined &&
    (replacing
      ? !warnsAspect || understood
      : trimmedName !== '' &&
        trimmedName.length <= FLOOR_PLAN_LIMITS.maxNameLength &&
        locationId !== '');

  function discardPreview(): void {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = undefined;
  }

  async function chooseFile(file: File | undefined): Promise<void> {
    const ticket = ++readCounter.current;
    discardPreview();
    setChosen(undefined);
    setFileError(undefined);
    setUnderstood(false);
    setError(undefined);
    if (!file) return;

    if (file.size > FLOOR_PLAN_LIMITS.maxImageBytes) {
      setFileError('Die Datei ist zu groß (höchstens 20 MB).');
      return;
    }
    let info: ImageInfo | undefined;
    try {
      const result = readImageInfo(await readFileBytes(file));
      if (ticket !== readCounter.current) return;
      if (isImageInfoFailure(result)) {
        setFileError(result.error);
        return;
      }
      const sizeProblem = checkPlanImageSize(result);
      if (sizeProblem) {
        setFileError(sizeProblem);
        return;
      }
      info = result;
    } catch {
      if (ticket === readCounter.current) setFileError('Die Datei konnte nicht gelesen werden.');
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    previewRef.current = previewUrl;
    setChosen({ file, info, previewUrl });
    if (!replacing) setName((current) => (current.trim() === '' ? baseName(file.name) : current));
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    void chooseFile(event.dataTransfer.files[0]);
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit || !chosen) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const response = replacing
        ? await replaceFloorPlanImage(customerId, replacing, chosen.file, chosen.info.mediaType)
        : await uploadFloorPlan(customerId, {
            locationId,
            name: trimmedName,
            file: chosen.file,
            mediaType: chosen.info.mediaType,
          });
      onDone(await withThumbnail(customerId, response.plan, chosen.file, chosen.info));
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : replacing
            ? 'Ersetzen fehlgeschlagen.'
            : 'Hochladen fehlgeschlagen.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  const title = replacing ? `Plan „${replacing.name}“ ersetzen` : 'Plan hochladen';
  const submitLabel = replacing ? 'Ersetzen' : 'Hochladen';

  return (
    <div className="card" role="dialog" aria-modal="false" aria-labelledby="plan-upload-title">
      <h2 id="plan-upload-title">{title}</h2>
      <form
        className="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {replacing ? null : (
          <>
            <div className="form-field">
              <label htmlFor="plan-name">Name</label>
              <input
                id="plan-name"
                type="text"
                maxLength={FLOOR_PLAN_LIMITS.maxNameLength}
                aria-required="true"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="form-field">
              <label htmlFor="plan-location">Ort</label>
              <select
                id="plan-location"
                aria-required="true"
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
              >
                <option value="">(bitte wählen)</option>
                {locationOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {'— '.repeat(option.depth)}
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div
          className={dragging ? 'dropzone dropzone--active' : 'dropzone'}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <label htmlFor="plan-file">Plan (PNG oder JPEG) auswählen oder hierher ziehen</label>
          <input
            id="plan-file"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(event) => void chooseFile(event.target.files?.[0])}
          />
          <p className="muted">Höchstens 20 MB.</p>
        </div>

        {fileError ? (
          <p className="notice notice--warning" role="alert">
            {fileError}
          </p>
        ) : null}

        {chosen ? (
          <div className="stack">
            <img className="plan-preview" src={chosen.previewUrl} alt="Vorschau des Plans" />
            <p className="muted">
              {FORMAT_LABELS[chosen.info.mediaType]} · {chosen.info.width} × {chosen.info.height} px
            </p>
          </div>
        ) : null}

        {warnsAspect ? (
          <div className="notice notice--warning" role="alert">
            <p>Platzierte Punkte können verrutschen, bitte prüfen.</p>
            <label>
              <input
                type="checkbox"
                checked={understood}
                onChange={(event) => setUnderstood(event.target.checked)}
              />{' '}
              Verstanden
            </label>
          </div>
        ) : null}

        {error ? (
          <p className="notice notice--warning notice--multiline" role="alert">
            {error}
          </p>
        ) : null}

        <p>
          <button className="button button--primary" type="submit" disabled={!canSubmit}>
            {submitting ? 'Lädt ...' : submitLabel}
          </button>{' '}
          <button
            type="button"
            className="button button--ghost"
            disabled={submitting}
            onClick={onCancel}
          >
            Abbrechen
          </button>
        </p>
      </form>
    </div>
  );
}
